import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import api from '../lib/api';
import {
  AlertTriangle, Mail, Plus, X, Send, Save, Clock, Users,
  CheckCircle2, AlertCircle, History,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

/** Lista editable de chips de email — agregar/quitar. Copia local (independiente
 * de la del padre) para no depender de `RecordatoriosPage`. */
function EmailChips({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email inválido');
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter(x => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map(e => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => remove(e)} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function AlegraDraftReminderSection() {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, h] = await Promise.all([
        api.get('/recordatorios/alegra-draft/config'),
        api.get('/recordatorios/alegra-draft/preview'),
        api.get('/recordatorios/alegra-draft/log?limit=10'),
      ]);
      setCfg(c.data);
      setPreview(p.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando recordatorio Alegra Draft');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/recordatorios/alegra-draft/config', cfg);
      setCfg(res.data);
      toast.success('Configuración guardada');
      api.get('/recordatorios/alegra-draft/preview').then(r => setPreview(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if ((!cfg.to || cfg.to.length === 0) && (!cfg.grupos || cfg.grupos.length === 0)) {
      toast.error('Agrega al menos un destinatario o grupo antes de enviar');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/recordatorios/alegra-draft/send-now');
      const d = res.data;
      if (d.sent) {
        toast.success(`Email enviado a ${d.to.length} destinatario(s) con ${d.count} factura(s) en draft`);
      } else {
        toast.info(`No enviado: ${d.reason || d.error || 'sin items'}`);
      }
      api.get('/recordatorios/alegra-draft/log?limit=10').then(r => setHistory(r.data?.items || []));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando');
    } finally {
      setSending(false);
    }
  };

  if (loading || !cfg) {
    return <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>;
  }

  const toggleGrupo = (key) => {
    const arr = new Set(cfg.grupos || []);
    if (arr.has(key)) arr.delete(key); else arr.add(key);
    setCfg({ ...cfg, grupos: Array.from(arr) });
  };
  const grupoActivo = (k) => (cfg.grupos || []).includes(k);
  const GRUPOS_DISPONIBLES = [
    { key: 'gerencia', label: 'Gerencia' },
    { key: 'corporativo', label: 'Corporativo' },
    { key: 'contabilidad', label: 'Contabilidad' },
  ];

  return (
    <>
      <Card data-testid="card-recordatorio-alegra-draft">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" /> Facturas en Alegra sin emitir al SII
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Email diario a Gerencia con las proformas ya facturadas en Alegra que llevan ≥ N días en <strong>DRAFT</strong> (sin timbrar en SII).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
              <Switch
                checked={cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                data-testid="alegra-draft-switch-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Umbral (días en draft)
              </label>
              <Input
                type="number" min={1} max={60}
                value={cfg.dias_min}
                onChange={(e) => setCfg({ ...cfg, dias_min: parseInt(e.target.value, 10) || 3 })}
                data-testid="alegra-draft-input-dias-min"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Alertar si lleva ≥ X días sin emitir.</p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Re-avisar cada (días)</label>
              <Input
                type="number" min={1} max={60}
                value={cfg.resend_days}
                onChange={(e) => setCfg({ ...cfg, resend_days: parseInt(e.target.value, 10) || 7 })}
                data-testid="alegra-draft-input-resend"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Evita spam: cada X días vuelve a incluir la misma factura.</p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Hora envío diario (24h)</label>
              <Input
                type="time"
                value={cfg.hora}
                onChange={(e) => setCfg({ ...cfg, hora: e.target.value })}
                data-testid="alegra-draft-input-hora"
              />
            </div>
          </div>

          <div className="pt-2 border-t">
            <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Grupos de destinatarios
            </label>
            <div className="flex flex-wrap gap-2">
              {GRUPOS_DISPONIBLES.map(g => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => toggleGrupo(g.key)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    grupoActivo(g.key)
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`alegra-draft-grupo-${g.key}`}>
                  {g.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Los emails de cada grupo se resuelven desde <strong>Configuración → Grupos de Email</strong>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
            <EmailChips
              label="Destinatarios extra (Para)"
              items={cfg.to || []}
              onChange={(t) => setCfg({ ...cfg, to: t })}
              placeholder="email@chirinosteel.com"
              testId="alegra-draft-to"
            />
            <EmailChips
              label="Copia (CC)"
              items={cfg.cc || []}
              onChange={(c) => setCfg({ ...cfg, cc: c })}
              placeholder="copia@chirinosteel.com"
              testId="alegra-draft-cc"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-3 border-t">
            <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="alegra-draft-btn-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
            <Button onClick={handleSendNow} disabled={sending} variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="alegra-draft-btn-send-now">
              <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Enviando…' : 'Enviar ahora (test, ignora dedupe)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="w-4 h-4" /> Vista previa Alegra Draft: {preview?.count || 0} factura(s)
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              Total pendiente: <strong className="text-red-700">{formatCLP(preview?.total || 0)}</strong>
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Proforma</th>
                  <th className="text-left px-3 py-2">Contrato</th>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-center px-3 py-2">Alegra ID</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-center px-3 py-2">Días</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.items || []).slice(0, 50).map((i, idx) => (
                  <tr key={idx} className="border-t hover:bg-muted/30" data-testid={`alegra-draft-preview-row-${i.numero_proforma}`}>
                    <td className="px-3 py-1.5 font-mono font-semibold text-teal-700">#{i.numero_proforma}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px]">{i.contrato_ref}</td>
                    <td className="px-3 py-1.5 truncate max-w-xs">{i.cliente_nombre || '—'}</td>
                    <td className="px-3 py-1.5 text-center font-mono text-blue-700">{i.alegra_invoice_id}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{formatCLP(i.total)}</td>
                    <td className="px-3 py-1.5 text-center text-red-700 font-bold">{i.dias}d</td>
                  </tr>
                ))}
                {(preview?.items?.length || 0) === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    No hay facturas Alegra en draft &gt;= {preview?.dias_min || cfg.dias_min} días 🎉
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><History className="w-4 h-4" /> Historial de envíos (Alegra Draft)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Aún no se han enviado emails.</div>
          ) : (
            <div className="divide-y">
              {history.map((h, idx) => (
                <div key={idx} className="px-4 py-2.5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    {h.success ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
                    <span>{new Date(h.timestamp).toLocaleString('es-CL')}</span>
                    <Badge variant="outline" className="text-[10px]">{h.manual ? 'manual' : 'auto'}</Badge>
                    <span className="text-muted-foreground">→ {(h.to || []).join(', ')}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {h.items_count} factura(s) {h.dedup_skipped ? `· ${h.dedup_skipped} omitida(s) por dedupe` : ''}
                    {h.error && <span className="text-red-600 ml-2">{h.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
